/**
 * El color de acento del panel y el editor.
 *
 * Es la misma mecánica que el modo oscuro, y a propósito: cookie compartida en
 * `.veltica.com` para que cruzar del panel al editor no cambie de color, copia
 * en `localStorage` para el primer pintado, y un atributo en `<html>` que es lo
 * único que las hojas de estilo miran. Ver `brand.css`, que declara qué hace
 * cada acento con los trece colores alternativos.
 *
 * Vive en `@webbuilder/ui` y no en cada aplicación —donde sí vive `theme.ts`,
 * copiado dos veces— porque es código nuevo y no hay razón para nacer duplicado:
 * las dos aplicaciones tienen que estar de acuerdo en qué color es cuál, y dos
 * listas de catorce opciones acaban siendo dos listas distintas.
 */
/** Los trece, más el de la casa. Los nombres son los del atributo en `<html>`. */
export const ACCENTS = [
    'azul', 'indigo', 'violeta', 'fucsia', 'rosa', 'rojo',
    'naranja', 'ambar', 'amarillo', 'verde', 'esmeralda', 'turquesa', 'cian', 'grafito',
];
/** El azul de producto que recibe quien todavía no eligió otro tono. */
export const DEFAULT_ACCENT = 'azul';
/**
 * Dónde vive el nombre de cada color.
 *
 * El nombre NO se escribe aquí: los catorce puntos sólo se distinguen por el
 * `title` y el `aria-label`, así que un nombre fijo en español es la única
 * parte del selector que no se traduce — y se nota justo donde más molesta, con
 * el panel entero en inglés. La clave reutiliza el slug del atributo de
 * `<html>` (`azul`, `ambar`, sin tildes) para que la lista de arriba siga
 * siendo la única lista de colores que hay.
 *
 * `@webbuilder/ui` no tiene traductor propio ni debe tenerlo: quien pinta los
 * puntos pasa el texto ya resuelto.
 */
export function accentLabelKey(accent) {
    return `accent.${accent}`;
}
/**
 * La muestra de cada color en el selector.
 *
 * Es el ÚNICO sitio donde las semillas se repiten fuera de `brand.css`, y no hay
 * forma de evitarlo: un punto de color en el selector no puede pintarse con
 * `var(--vl-accent-seed)` porque los catorce se dibujan a la vez y esa variable
 * sólo tiene un valor, el activo. Si aquí y allí se separan, el punto miente
 * sobre lo que hace — así que cualquier cambio va a los dos sitios.
 *
 * Retocados el 2026-10-07: antes eran tonos apagados y casi todos tierra
 * (oliva, bosque, pino, petróleo, acero se parecían entre sí). Ahora van más
 * vivos y repartidos por la rueda, cada uno con contraste ≥ 4.5:1 con el
 * texto blanco que llevan encima los botones. Los nombres internos no
 * cambiaron, para no perder el color que alguien ya tenía guardado.
 */
export const ACCENT_SWATCHES = {
    azul: '#2563eb',
    indigo: '#4f46e5',
    violeta: '#7c3aed',
    fucsia: '#b42aa5',
    rosa: '#d03a78',
    rojo: '#d23b3b',
    naranja: '#c65018',
    ambar: '#ae6309',
    amarillo: '#927009',
    verde: '#2a863c',
    esmeralda: '#0e8565',
    turquesa: '#0e838e',
    cian: '#0a7dae',
    grafito: '#475569',
};
const STORAGE_KEY = 'veltica_accent';
const COOKIE = 'veltica_accent';
/**
 * Lo que se anuncia al cambiar de color dentro de una misma página.
 *
 * Hace falta porque el color se elige desde DOS sitios —la mascota del Resumen y
 * el menú de usuario— y los dos pintan cuál está puesto. `persistAccent` cambia
 * el atributo de `<html>`, así que el panel entero se recolorea; pero el sitio
 * que no tocaste seguía marcando el color anterior, porque su idea de cuál era
 * el activo la había leído al montarse y nadie se la volvió a contar.
 */
const CHANGE_EVENT = 'veltica:accent';
function isAccent(value) {
    return ACCENTS.includes(value);
}
function cookieAccent() {
    const entry = document.cookie
        .split('; ')
        .find((part) => part.startsWith(`${COOKIE}=`));
    const value = entry?.slice(COOKIE.length + 1);
    return isAccent(value) ? value : null;
}
/** El elegido, o el de la casa. La cookie manda: es la que cruza subdominios. */
export function readAccent() {
    const shared = cookieAccent();
    if (shared)
        return shared;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isAccent(stored))
            return stored;
    }
    catch {
        /* En una ventana privada no hay almacenamiento; se queda el de la casa. */
    }
    return DEFAULT_ACCENT;
}
/**
 * Lo aplica y lo guarda.
 *
 * El atributo se quita en vez de escribirse cuando es el de la casa: así el
 * defecto de `:root` es el defecto de verdad, y una pestaña sin preferencia y
 * otra que eligió «azul» pintan exactamente lo mismo.
 */
export function persistAccent(accent) {
    const root = document.documentElement;
    if (accent === DEFAULT_ACCENT)
        root.removeAttribute('data-accent');
    else
        root.setAttribute('data-accent', accent);
    try {
        localStorage.setItem(STORAGE_KEY, accent);
    }
    catch {
        /* La cookie sigue cubriendo la navegación normal entre subdominios. */
    }
    const domain = location.hostname === 'veltica.com' || location.hostname.endsWith('.veltica.com')
        ? '; Domain=.veltica.com'
        : '';
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE}=${accent}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`;
    /* Y se avisa: lo escrito ya está en el DOM, así que quien escuche y vuelva a
       leer se encuentra el valor nuevo. */
    window.dispatchEvent(new Event(CHANGE_EVENT));
}
/**
 * Avisa cada vez que el acento cambia, aquí o en otra pestaña.
 *
 * `storage` cubre la segunda pestaña: allí `persistAccent` no corrió, pero sí
 * escribió `localStorage`, y el navegador lo cuenta a las demás. La cookie no
 * avisa de nada —no hay evento para ella— y por eso no basta con ser la fuente
 * que manda al leer.
 */
export function subscribeAccent(listener) {
    window.addEventListener(CHANGE_EVENT, listener);
    window.addEventListener('storage', listener);
    return () => {
        window.removeEventListener(CHANGE_EVENT, listener);
        window.removeEventListener('storage', listener);
    };
}
/**
 * Lo aplica al arrancar, sin guardar nada.
 *
 * Se llama antes de montar React: el atributo tiene que estar puesto en el
 * primer pintado o el panel entra en el azul de la casa y salta al color
 * elegido un fotograma después.
 */
export function applyStoredAccent() {
    const accent = readAccent();
    const root = document.documentElement;
    if (accent === DEFAULT_ACCENT)
        root.removeAttribute('data-accent');
    else
        root.setAttribute('data-accent', accent);
    return accent;
}
//# sourceMappingURL=accent.js.map